/**
 * 파일명 : com0109m.tsx
 * 설명 : 결재문서 상신 화면
 * 수정내역
 ************************************************
 * Version	   수정일자			수정자	    수정내용      
 ************************************************
 * 1.0		2020-08-11		윤성수	    최초작성
 * 1.1      2023-03-27      최희원      증명서상세화면 신규에 따른 불필요로직 삭제
 **/

/************************************************
 * Import 영역  
 ************************************************/
/* React */
import React from 'react';
import { isMobile } from 'react-device-detect';
import { useReactToPrint } from 'react-to-print';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Com0109mAction } from '../../../redux/slice/com/com0109m_slice';
import { IApprItemState, IApprRefUsrItemState, IAttachFileItemState, IComCdItemState } from '../../../redux/models/com/com0109m_model';

/* AntD */
import { Layout, Row, Col, Space, Descriptions, Input, Button, Upload, Typography, Table, Select } from 'antd';
import { SearchOutlined, UploadOutlined, PaperClipOutlined, DeleteOutlined } from '@ant-design/icons';
import { Editor } from '@tinymce/tinymce-react';

/* 업무 */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';
import parse from 'html-react-parser';

const Com0109m = (props:any) => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    let tmp = 0;
    const editorHeightDiff = 560;

	/** State / Ref / Reducer 선언 **/
    const dispatch = useAppDispatch();
    const apprDcdList     = useAppSelector((state) => state.Com0109m.apprDcdList);
    const apprItems       = useAppSelector((state) => state.Com0109m.apprItems);
    const isNotAuth       = useAppSelector((state) => state.Com0109m.isNotAuth);
    const apprOpns        = useAppSelector((state) => state.Com0109m.apprOpns);
    const approvalList    = useAppSelector((state) => state.Com0109m.approvalList);
    const refList         = useAppSelector((state) => state.Com0109m.refList);
    const attachFileItems = useAppSelector((state) => state.Com0109m.attachFileItems);
    const fileList        = useAppSelector((state) => state.Com0109m.fileList);
    const editorRef = React.useRef(null);
    

    /** Render 변수 선언영역 **/
    const componentRef = React.useRef(null);
    const [editorHeight, setEditorHeight] = React.useState<number>(window.innerHeight - editorHeightDiff); // 기본 화면 높이에서 400px을 빼기
    const [editorKey, setEditorKey] = React.useState<number>(0);

    /* AntD */
    const { Content } = Layout;
    const { Title } = Typography;
    
    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     **/
    React.useEffect(() => {
        const handleResize = () => {
            setEditorHeight(window.innerHeight - editorHeightDiff); // 화면 크기 변경 시 에디터 높이 업데이트

            let tmpKey = ++tmp;
            tmp = tmpKey;
            setEditorKey(tmpKey);
        };
      
        window.addEventListener('resize', handleResize);

        const option = {
            useYn: 'ALL',
            callback: (datas:IComCdItemState[]) => {
                let arrOptions:IComCdItemState[] = [];
                datas.forEach((item:IComCdItemState) => {
                    if (item.cd !== '001' && item.attr1 === 'N') {
                        arrOptions.push({
                            label : item.cdNm,
                            value : item.cd,
                            attr1 : item.attr1,
                            attr2 : item.attr2,
                            cd    : item.cd,
                            cdNm  : item.cdNm,
                            rmk1  : item.rmk1,
                            useYn : item.useYn,
                        });
                    }
                });

                dispatch(Com0109mAction.setApprDcdList(arrOptions));
            }
        };
        common.getComCodeList('014', option);

        if (!common.isEmptyString(props.apprNo)) {
            fn_Search(props.apprNo);
        } else {
            dispatch(Com0109mAction.setApprItems({ ...apprItems, empNo : common.getEmpNo(), reqUserNm : common.getUserNm() }));
        }

        return () => {
            dispatch(Com0109mAction.initState());
            window.removeEventListener('resize', handleResize); // cleanup
        };
    }, []);


    const fn_Search = (apprNo:string) => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            apprNo : apprNo
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRCOMR0008',
            success: (responseData:any) => {
                let filtered = responseData.approvalList.filter((e:any) => {
                    return e.apprOrd === responseData.apprItems.currApprOrd;
                });
                
                filtered = filtered && filtered.length > 0 ? filtered[0] : null;

                dispatch(Com0109mAction.setApprItems(responseData.apprItems));
                dispatch(Com0109mAction.setApprovalList(responseData.approvalList));
                dispatch(Com0109mAction.setRefList(responseData.apprRefItems));
                dispatch(Com0109mAction.setAttachFileItems(responseData.attachFileItems));
                dispatch(Com0109mAction.setIsNotAuth(filtered.apprEmpNo !== common.getEmpNo() || filtered.apprStCd !== '01'));

                const apprOpns = !responseData.approvalList || responseData.approvalList.length < 1 ? null : responseData.approvalList.filter((e:any) => {
                    return e.apprOpn !== null && e.apprOpn !== '';
                });
                dispatch(Com0109mAction.setApprOpns(apprOpns));
            },
        });
    }

	
	/**
     * 기능 : 초기화 함수
     * param : 
     **/
    const fn_Reset = async () => {
        dispatch(Com0109mAction.initState());
    };

	/**
     * 기능 : 값 변경 이벤트
     * 설명 : 입력컴포넌트
     * param : 
     * return : None
     */
     const inputOnChange = React.useCallback((e:any) => {
        const newApprItems = {...apprItems, [e.currentTarget.name] : e.currentTarget.value};
        dispatch(Com0109mAction.setApprItems(newApprItems));
    }, [apprItems]);

	/**
     * 기능 : 반려버튼 클릭
     * 설명 : 반려버튼 클릭
     * param : 
     * return : None
     */
     const onBtnReturnClick = () => {
        const accept = (data:string) => {
            const param = {
                title : '위드정보 확인',
                contents: '반려처리 하시겠습니까?',
                accept : () => {
                    /* 변수선언 및 셋팅 영역 */
                    const requestData = {
                        // 입력파라미터
                        ...apprItems,
                        apprD : [ { apprOpn : data } ]
                    };
            
                    commonProxy.callAsyncSvc(requestData, {
                        txCd : 'TRCOMS0008',
                        success : (responseData:any) => {
                            const param = {
                                title : '위드정보 확인',
                                contents: '반려처리 되었습니다.',
                            };
            
                            common.openAlert(param);
                            fn_Search(responseData.apprItems.apprNo);
                        },
                    });
                }
            };
            common.openConfirm(param);
        }
        const params:any = {};
        params.title = '반려의견 입력';
        params.popupId = 'com9998p';
        params.props = { width: 700 };
        params.width = 700;
        params.accept = accept;
        common.openPopup(params);
    }

    const fn_ReqAppr = (filterList:IApprRefUsrItemState[]) => {
        const save = (fileItems:IAttachFileItemState[]) => {
            /* 변수선언 및 셋팅 영역 */
            let requestData;
            if (fileItems) {
                const arrAttachFileItems: IAttachFileItemState[] = attachFileItems.filter((item:IAttachFileItemState) => {
                    return fileItems.filter((file:IAttachFileItemState) => {
                        return file.orginFileNm === item.orginFileNm;
                    }).length < 1;
                });
                requestData = { ...apprItems, apprD : filterList, apprRef: refList, fileItems : [...arrAttachFileItems, ...fileItems] };
            } else {
                requestData = { ...apprItems, apprD : filterList, apprRef: refList };
            }
            /* 트렌젝션 호출 및 결과 셋팅 */
            commonProxy.callAsyncSvc(requestData, {
                txCd : 'TRCOMS0005',
                success: (responseData:any) => {
                    const param = {
                        title : '위드정보 확인',
                        contents: '상신 처리 되었습니다.',
                        accept: () => {
                            fn_Search(responseData.apprItems.apprNo);
                        }
                    };

                    common.openAlert(param);
                },
            });
        }
        
        if (fileList && fileList.length > 0) {
            requestUploadFiles({
                callback : (data:any) => {
                    save(data.fileItems);
                }
            });
        } else {
            save(null);
        }
    }


    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
     const onClick = async (e:any) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case "btn_reset": {
                fn_Reset();
                break;
            }
            case 'btn_approval': {
                const accept = (data:[]) => {
                    dispatch(Com0109mAction.setApprovalList(data));
                }
                const params:any = {};
                params.title = '결재자 선택';
                params.popupId = 'com0101p1';
                params.accept = accept;
                params.width = 1350
                common.openPopup(params);
                break;
            }

            case 'btn_ref': {
                const accept = (data:[]) => {
                    dispatch(Com0109mAction.setRefList(data));
                }
                const params:any = {};
                params.title = '참조자 선택';
                params.popupId = 'com0101p2';
                params.accept = accept;
                params.width = 1350
                common.openPopup(params);
                break;
            }
            
            case "btn_return": {
                onBtnReturnClick();

                break;
            }
            
            case "btn_appr": {
                const param = {
                    title : '위드정보 확인',
                    contents: '결재처리 하시겠습니까?',
                    accept : () => {
                        /* 변수선언 및 셋팅 영역 */
                        const requestData = {
                            // 입력파라미터
                            ...apprItems
                        };
                
                        commonProxy.callAsyncSvc(requestData, {
                            txCd : 'TRCOMS0006',
                            success : (responseData:any) => {
                                const param = {
                                    title : '위드정보 확인',
                                    contents: '승인 처리 되었습니다.',
                                };
                
                                common.openAlert(param);

                                fn_Search(responseData.apprItems.apprNo);
                            },
                        });
                    }
                };
                common.openConfirm(param);
                break;
            }

            case "btn_req": {
                if(common.isEmptyString(apprItems.apprDcd)){
                    const param = {
                        title : '위드정보 확인',
                        contents: '결재문서를 선택하세요.',
                    };

                    common.openInfo(param);
                    return
                }
                
                let filterList:IApprRefUsrItemState[];

                if (approvalList && approvalList.length > 0) {
                    filterList = approvalList.filter((item:IApprRefUsrItemState) => {
                        return item.apprEmpNo !== common.getEmpNo();
                    });
                }

                if (!filterList || filterList.length < 1) {
                    const param = {
                        title : '위드정보 확인',
                        contents: '결재선을 지정하세요.',
                    };

                    common.openInfo(param);
                    return;
                }

                const param = {
                    title : '위드정보 확인',
                    contents: '상신 하시겠습니까?',
                    accept : () => {
                        fn_ReqAppr(filterList);
                    }
                };
                common.openConfirm(param);
                break;
            }

            case "btn_cls": {
                const param = {
                    title : '위드정보 확인',
                    contents: '상신취소 하시겠습니까?',
                    accept : () => {
                        /* 변수선언 및 셋팅 영역 */
                        const requestData = {
                            // 입력파라미터
                            ...apprItems
                        };
                
                        commonProxy.callAsyncSvc(requestData, {
                            txCd : 'TRCOMS0007',
                            success : (responseData:any) => {
                                const param = {
                                    title : '위드정보 확인',
                                    contents: '상신취소 처리 되었습니다.',
                                };
                
                                common.openAlert(param);
                                fn_Reset();
                            },
                        });
                    }
                };
                common.openConfirm(param);
                break;
            }

            case "btn_apprOpns": {
                const params:any = {};
                params.title = '결재의견';
                params.popupId = 'com0109p';
                params.props = { apprOpns: approvalList };
                params.width = 700;
                common.openPopup(params);
                break;
            }

            default:
        }
    };

    /************************************************
     * 개발자 함수 영역  
     ************************************************/
    const onDelete = (item:any) => {
        let num = fileList.findIndex((e:any) => {
            return e.fileNm === item.name;
        });
        dispatch({type : 'REMOVE_ARRAY_ELEMENTS', data_node : 'fileList', idx : [num]});
        num = attachFileItems.findIndex(e => {
            return e.fileNm === item.fileNm;
        });
        dispatch({type : 'REMOVE_ARRAY_ELEMENTS', data_node : 'attachFileItems', idx : [num]})
    }

   /** 
    * 기능 : antd dummy 업로드 흐름 관리
    * param : onSuccess
    * return : none
    */
     const dummyRequest = ({ file, onSuccess }:any) => {  	// 업로드 흐름 관리
        setTimeout(() => {
            if (fileList.filter((e:any) => {
                return e.name === file.name;
            }).length < 1) {
                onSuccess('ok');
            }
        }, 0);
    };

    /**
     * 기능 : 파일들을 Back으로 보내 업로드 한다.
     * param :
     * return : None
     */
     const requestUploadFiles = (options:any) => {
        common.uploadFile(fileList, {
            sourceDcd: '02',
            sourceId: 0,
            success: (response:any) => {
                if(typeof options.callback === 'function'){
                    options.callback(response);
                }
            },
        });
    }

    /**
     * 기능 : 선택한 파일중 done인 파일을 fileList에 추가한다.
     * param :
     * return : None
     */
     const selectFile_onChange = (info:any) => {
        switch (info.file.status) {
            case 'done':
                const grdFile = { fileNm : info.file.originFileObj.name, orginFileNm : info.file.originFileObj.name };
                dispatch(Com0109mAction.addFileList(info.file.originFileObj));
                dispatch(Com0109mAction.addAttachFileItems(grdFile));
                break;

            default:
                break;
        }
    }


    /**
     * 기능 : upload버튼의 기능 할당
     * param :
     * return : None
     */
    /* File Upload Options */
    const uploadProps = {
        onChange: selectFile_onChange,
        multiple: true,                  // 파일 다중 선택
        showUploadList: false, 		     // 업로드된 파일 리스트 보기
    };

    /**
     * 기능 : fn_CurrApprDcd
     * 설명 : 요청일자계산
     * param : 
     * return : None
     **/
     const fn_CurrApprDcd = () => {
        let item:IComCdItemState;
         
        if (!apprDcdList || apprDcdList.length < 1) {
             item = { cd : '', cdNm: '', value : '', label : '', attr1 : '', attr2: '', useYn: '' };
        } else {
            let apprDcd = apprItems.apprDcd;
    
            if (common.isEmptyString(apprItems.apprDcd)) {
                return {} as any;
            }
    
            item = apprDcdList.filter((e:IComCdItemState) => {
                return e.value === apprDcd;
            })[0];
        }

        return item;
    }

    const handlePrint = useReactToPrint({
        contentRef: componentRef,
        documentTitle: '출력_문서',
    });

	return (
        /* 화면 디자인 영역 */
        <Layout>    
            <Content>
                <Row justify='end'>
                    <Col>
                        <Space>
                            {!common.isEmptyString(apprItems.apprNo) ?
                            <Button name='btn_print' className='background-color-default-button' onClick={() => handlePrint?.()}>출력</Button>
                            : null}
                            <Button
                                name="btn_reset"
                                className="background-color-default-button"
                                onClick={onClick}
                            >
                                초기화
                            </Button>
                            {apprOpns && apprOpns.length > 0 ?
                            <Button
                                name="btn_apprOpns"
                                className="background-color-default-button"
                                onClick={onClick}
                            >
                                결재의견
                            </Button> : null}
                            {!apprItems.apprNo ?
                            <>
                            <Button
                                name="btn_approval"
                                className="background-color-default-button"
                                icon={<SearchOutlined />}
                                onClick={onClick}
                                disabled={!common.isEmptyString(apprItems.apprNo)}
                            >
                                결재선
                            </Button>
                            <Button
                                name="btn_ref"
                                className="background-color-default-button"
                                icon={<SearchOutlined />}
                                onClick={onClick}
                                disabled={!common.isEmptyString(apprItems.apprNo)}
                            >
                                참조자
                            </Button>
                            </>
                            : null}
                            {!isNotAuth ?
                            <>
                            <Button
                                name="btn_return"
                                className="background-color-primary-button"
                                onClick={onClick}
                            >
                                반려
                            </Button>
                            <Button
                                name="btn_appr"
                                className="background-color-primary-button"
                                onClick={onClick}
                            >
                                결재
                            </Button>
                            </>
                            : null}
                            {(apprItems.apprNo && apprItems.reqEmpNo === common.getEmpNo() && apprItems.apprStCd === '01') ?
                            <Button
                                name="btn_cls"
                                className="background-color-primary-button"
                                onClick={onClick}
                                disabled={!(apprItems.apprNo && apprItems.reqEmpNo === common.getEmpNo() && apprItems.apprStCd === '01')}
                            >
                                상신취소
                            </Button>
                            : null}
                            {!apprItems.apprNo ?
                            <Button
                                name="btn_req"
                                className="background-color-primary-button"
                                onClick={onClick}
                                disabled={!common.isEmptyString(apprItems.apprNo)}
                            >
                                상신
                            </Button>
                            : null}
                        </Space>
                    </Col>
                </Row>
                <div ref={componentRef} style={{marginTop: 20, marginLeft: 20, marginRight: 20}}>
                    <Row justify='center'>
                        <Col>
                            <Title level={3}>{fn_CurrApprDcd().label}</Title>
                        </Col>
                    </Row>
                    <br/>
                    <Row justify='end'>
                        <Space size="middle">
                            <Col>
                                <Descriptions bordered size="small" layout="vertical" column={5}>
                                    <Descriptions.Item label="신청자" key={apprItems.empNo}>{apprItems.reqUserNm}<br/>{apprItems.apprNo ? apprItems.apprStCd === '08' ? "(취소)" : "(상신)" : "(대기)"}</Descriptions.Item>
                                    {
                                        approvalList.map(e => {
                                            return <Descriptions.Item label="승인자" key={e.apprEmpNo}>{e.apprUserNm}<br/>({e.apprStNm})</Descriptions.Item>
                                        })
                                    }
                                    {!approvalList || approvalList.length < 1 ? <Descriptions.Item label="승인자"> </Descriptions.Item> : null}
                                </Descriptions>
                            </Col>
                            {refList && refList.length > 0 ?
                            <Col>
                                <Descriptions bordered size="small" layout="vertical" column={5}>
                                    {
                                        refList.map(e => {
                                            return <Descriptions.Item label="참조자" key={e.refEmpNo}>{e.refUserNm}<br/>({e.chkYn ? e.chkYn === "Y" ? "읽음" : "안읽음" : "대기"})</Descriptions.Item>
                                        })
                                    }
                                </Descriptions>
                            </Col> : null}
                        </Space>
                    </Row>
                    <Row style={{marginTop: 10, display:'flex'}}>
                        <Col style={{width:'100%'}}>
                            <Descriptions bordered column={2} size="small">
                                <Descriptions.Item label="신청인">{apprItems.reqUserNm}</Descriptions.Item>
                                <Descriptions.Item label="결재문서구분">
                                    {!common.isEmptyString(apprItems.apprNo) ? fn_CurrApprDcd().label
                                    :
                                    <Select
                                        placeholder='결재문서를 선택하세요.'
                                        style={{width: 300}}
                                        options={apprDcdList}
                                        onChange={val => {
                                            const curr = apprDcdList.filter((item:IComCdItemState) => {
                                                return item.value === val;
                                            });
                                            if (curr && curr.length > 0 && curr[0].useYn !== 'Y') {
                                                const param = {
                                                    title : '위드정보 확인',
                                                    contents: '사용중지된 결재문서 입니다.\n관리자에게 문의하세요.',
                                                };
                            
                                                common.openInfo(param);
                                                return
                                            }
                                            dispatch(Com0109mAction.setApprItems({...apprItems, apprDcd: val, cntnt: curr && curr.length > 0 ? curr[0].rmk1 : ''}));
                                        }}
                                        value={apprItems.apprDcd}

                                        /* ReadOnly 적용 */
                                        {...common.getReadonlySelectboxProps(!common.isEmptyString(apprItems.apprNo))}
                                    ></Select>}
                                </Descriptions.Item>
                                <Descriptions.Item label="제목" span={2}>
                                    {!common.isEmptyString(apprItems.apprNo) ? apprItems.title
                                    : <Input id="inpuTtitle" name='title' value={apprItems.title} style={{ width: '100%'}} onChange={inputOnChange}/>}
                                </Descriptions.Item>
                                <Descriptions.Item label="파일첨부" span={2}>
                                    {!apprItems.apprNo ?
                                    <Upload
                                        {...uploadProps}
                                        id="btnUpload"
                                        name="btnUpload"
                                        customRequest={dummyRequest}
                                        disabled={!common.isEmptyString(apprItems.apprNo)}
                                        >
                                        <Button size="small" icon={<UploadOutlined/>}>파일첨부</Button>
                                    </Upload>
                                    : null }
                                    &nbsp;&nbsp;&nbsp;
                                    {attachFileItems && attachFileItems.length > 0 ? attachFileItems.map((result) => {
                                        return (<><PaperClipOutlined/><a onClick={() => { if (apprItems.apprNo) common.downloadFile(result.id) }}>{result.orginFileNm}</a>{!apprItems.apprNo ? <Button name="btn_del" icon={<DeleteOutlined/>} className="link-button" size="small" onClick={() => onDelete(result)}></Button> : null}&nbsp;&nbsp;&nbsp;</>)}) : ''  // eslint-disable-line jsx-a11y/anchor-is-valid
                                    }
                                </Descriptions.Item>
                            </Descriptions>
                        </Col>
                    </Row>
                    {!common.isEmptyString(apprItems.apprNo) ?
                        <>
                        <div key={editorKey}>
                            <Descriptions bordered column={1} size='small' style={{display:'flex', marginTop: 10}}>
                                <Descriptions.Item labelStyle={{ display: 'none' }}>
                                    {parse(apprItems.cntnt)}
                                </Descriptions.Item>
                            </Descriptions>
                        </div> </>:
                        <Row style={{marginTop: 10 }}>
                            <Col style={{width:'100%'}}>
                                <Editor
                                    key={editorKey}
                                    id='editorBoard'
                                    apiKey={common.editorKey}
                                    onInit={(_, editor) => {
                                        editorRef.current = editor;
                                        document.getElementsByClassName('tox-statusbar')[0].remove();
                                    }}
                                    value={apprItems.cntnt}
                                    init={{
                                        resize:false,
                                        toolbar_mode: 'sliding',
                                        language:'ko_KR',
                                        height: editorHeight,
                                        menubar: false,
                                        plugins: [
                                            'advlist','autolink',
                                            'lists','link','image','charmap','preview','anchor','searchreplace','visualblocks',
                                            'fullscreen','insertdatetime','table'
                                            ],
                                        toolbar: 'undo redo | fontfamily fontsize | bold italic backcolor',
                                        content_style: 'body { font-family:굴림; font-size:12pt }',
                                        font_formats:'본고딕=Noto sans KR;돋움=Dotum;돋움체=DotumChe;굴림=Gulim;굴림체=GulimChe;바탕=Batang;바탕체=BatangChe;궁서=Gungsuh;궁서체=GungsuhChe;새굴림=New Gulim;맑은 고딕=Malgun Gothic;나눔 고딕=Nanum Gothic;나눔 명조=Nanum Myoungjo'
                                    }}
                                    onEditorChange={
                                        () => {
                                            dispatch(Com0109mAction.setApprItems({...apprItems, cntnt: editorRef.current.getContent()}));
                                        }
                                    }
                                />
                            </Col>
                        </Row>}
                    <br/>
                    <Row justify='center'>
                        <Col>상기와 같은 사유로 인하여 {fn_CurrApprDcd().label}을(를) 제출하오니 재가바랍니다.</Col>
                    </Row>
                </div>
            </Content>
        </Layout>
    );
}

export default Com0109m;
